/**
 * History router. Every screen has a stable URL (docs/52 §2.2) so pushes and emails deep-link into it.
 * - Links are plain <a href>; a document-level click handler turns same-origin clicks into pushState.
 * - Back returns to the same scroll position (docs/52 D4): positions are kept per history entry.
 * - After a navigation, focus moves to the screen title so screen readers hear where they are.
 * - `/l/<location>/...` scopes any screen to one location (docs/52 §2.2).
 */
import { createStore } from './store.ts';

export interface Loc {
  path: string;
  query: URLSearchParams;
  /** Location scope from a `/l/<id>` prefix, or null for "All locations". */
  scope: string | null;
  key: string;
}

function read(): Loc {
  const url = new URL(window.location.href);
  let path = url.pathname.replace(/\/+$/, '') || '/';
  let scope: string | null = null;
  const m = /^\/l\/(loc_[a-z0-9]{6,40})(\/.*)?$/.exec(path);
  if (m) {
    scope = m[1]!;
    path = m[2] || '/';
  }
  const key = (history.state as { key?: string } | null)?.key ?? 'root';
  return { path, query: url.searchParams, scope, key };
}

export const location = createStore<Loc>(typeof window === 'undefined' ? { path: '/', query: new URLSearchParams(), scope: null, key: 'root' } : read());

const SCROLL_KEY = 'ff-scroll';
function scrolls(): Record<string, number> {
  try {
    return JSON.parse(sessionStorage.getItem(SCROLL_KEY) ?? '{}') as Record<string, number>;
  } catch {
    return {};
  }
}
function saveScroll() {
  try {
    const s = scrolls();
    s[location.get().key] = window.scrollY;
    sessionStorage.setItem(SCROLL_KEY, JSON.stringify(s));
  } catch {
    /* storage can be full or blocked */
  }
}

let pendingRestore: number | null = null;
export function takeScrollRestore(): number | null {
  const v = pendingRestore;
  pendingRestore = null;
  return v;
}

export function scoped(path: string, scope: string | null = location.get().scope): string {
  if (!scope || !path.startsWith('/') || path.startsWith('/l/') || path.startsWith('/account') || path.startsWith('/signin') || path.startsWith('/notifications')) return path;
  return `/l/${scope}${path === '/' ? '' : path}`;
}

export function navigate(to: string, opts: { replace?: boolean } = {}): void {
  if (!to.startsWith('/') || to.startsWith('//')) {
    window.location.assign(to);
    return;
  }
  saveScroll();
  const key = Math.random().toString(36).slice(2, 10);
  if (opts.replace) history.replaceState({ key }, '', to);
  else history.pushState({ key }, '', to);
  pendingRestore = 0;
  location.set(read());
}

export function back(fallback: string): void {
  const st = history.state as { key?: string } | null;
  if (st?.key && st.key !== 'root') history.back();
  else navigate(fallback, { replace: true });
}

if (typeof window !== 'undefined') {
  if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
  if (!(history.state as { key?: string } | null)?.key) history.replaceState({ key: 'root' }, '');
  window.addEventListener('popstate', () => {
    const next = read();
    pendingRestore = scrolls()[next.key] ?? 0;
    location.set(next);
  });
  window.addEventListener('pagehide', saveScroll);
  document.addEventListener('click', (e) => {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    const a = (e.target as Element | null)?.closest?.('a');
    if (!a || !a.href || a.target || a.hasAttribute('download') || a.getAttribute('rel')?.includes('external')) return;
    const url = new URL(a.href);
    if (url.origin !== window.location.origin) return;
    // Server routes (the API, media, the export download) are real navigations, never app screens.
    if (url.pathname.startsWith('/api/') || url.pathname.startsWith('/media/') || url.pathname.startsWith('/demo/') || url.pathname.startsWith('/_ff/')) return;
    e.preventDefault();
    navigate(url.pathname + url.search);
  });
}

/** Match `/leads/:id` against a path. */
export function match(pattern: string, path: string): Record<string, string> | null {
  const ps = pattern.split('/');
  const xs = path.split('/');
  if (ps.length !== xs.length) return null;
  const out: Record<string, string> = {};
  for (let i = 0; i < ps.length; i++) {
    const p = ps[i]!;
    const x = xs[i]!;
    if (p.startsWith(':')) {
      if (!x) return null;
      out[p.slice(1)] = decodeURIComponent(x);
    } else if (p !== x) return null;
  }
  return out;
}
